import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  List, 
  RefreshCw, 
  Search, 
  Filter, 
  ChevronRight, 
  ChevronLeft, 
  User, 
  MapPin, 
  Package, 
  Calendar, 
  Building2,
  Loader2,
  Radio,
  Download,
  ExternalLink,
  Users,
  PieChart,
  BarChart
} from 'lucide-react';
import { collection, query, where, getDocs, doc, writeBatch, getDoc } from 'firebase/firestore';
import { firestore } from '../lib/firebase/config';
import { OperationEntry } from '../types/operationList';
import { useLanguage } from '../contexts/LanguageContext';
import { usePilgrimGuideStore } from '../stores/pilgrimGuideStore';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { LoadingProgress } from '../components/common/LoadingProgress';
import { BulkTransferOperatorModal } from '../components/operationList/BulkTransferOperatorModal';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';

export const MyOperationListPage = () => {
  const { translations } = useLanguage();
  const { 
    links, 
    loading: linksLoading, 
    initialized,
    progress,
    fetchLinks 
  } = usePilgrimGuideStore();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedOrganizer, setSelectedOrganizer] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [syncProgress, setSyncProgress] = useState({ current: 0, total: 0 });
  const [operationList, setOperationList] = useState<Record<string, OperationEntry>>({});
  const [isOperationLoading, setIsOperationLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [operatorOrganizers, setOperatorOrganizers] = useState<Record<string, { id: string, name: string }>>({});
  const [organizers, setOrganizers] = useState<{id: string, name: string}[]>([]);
  const [isBulkTransferModalOpen, setIsBulkTransferModalOpen] = useState(false);
  const [showStats, setShowStats] = useState(true);
  
  const itemsPerPage = 50;
  const userRole = localStorage.getItem('userRole');
  const userId = localStorage.getItem('userId');
  
  // Define determineRoleForUser function BEFORE it's used anywhere
  const determineRoleForUser = (entry: OperationEntry) => {
    if (!userId) return 'unknown';
    
    const isOwner = entry.isOwner !== false; // Default to true if not explicitly false
    
    // For guide view
    if (userRole === 'guide') {
      const isGuideTheOwner = entry.guideId === userId;
      const isGuideTheOperator = entry.operatorId === userId;
      
      if (isGuideTheOwner && isGuideTheOperator) {
        return isOwner ? 'owner and operator' : 'not owner and operator';
      } else if (isGuideTheOwner) {
        return isOwner ? 'owner' : 'not owner';
      } else if (isGuideTheOperator) {
        return 'operator';
      }
    }
    
    // For organizer view
    if (userRole === 'organizer') {
      const isOrganizerTheOwner = entry.organizerId === userId;
      const isOrganizerTheOperator = entry.operatorOrganizerId === userId;
      
      if (isOrganizerTheOwner && isOrganizerTheOperator) {
        return isOwner ? 'owner and operator' : 'not owner and operator';
      } else if (isOrganizerTheOwner) {
        return isOwner ? 'owner' : 'not owner';
      } else if (isOrganizerTheOperator) {
        return 'operator';
      }
    }
    
    return 'unknown';
  };

  // Determine role for admin view (objective perspective)
  const determineRole = (entry: OperationEntry) => {
    // Default isOwner to true if not explicitly false
    const isOwner = entry.isOwner === false ? false : true;
    
    // Compare organizer names
    const guideOrganizerName = entry.organizerName;
    const operatorOrganizerName = entry.operatorOrganizerName;
    
    // If operator is same as guide or no operator specified
    if (!entry.operatorId || entry.operatorId === entry.guideId) {
      return isOwner ? 'owner' : 'not owner';
    }
    
    // If organizers are the same
    if (guideOrganizerName === operatorOrganizerName) {
      return isOwner ? 'owner and operator' : 'not owner and operator';
    }
    
    // If this is an entry specifically for the operator role
    if ((entry as any).displayRole === 'operator') {
      return 'operator';
    }
    
    // If this is an entry specifically for the owner role
    return isOwner ? 'owner' : 'not owner';
  };
  
  // Initialize data
  useEffect(() => {
    if (!initialized) {
      fetchLinks();
    }
    fetchOperationList();
    fetchGuides();
    if (userRole === 'admin') {
      fetchOrganizers();
    }
  }, [initialized, fetchLinks, userRole]);

  // Fetch organizers for filter (admin only)
  const fetchOrganizers = async () => {
    if (userRole !== 'admin') return;
    
    try {
      const organizersRef = collection(firestore, 'Organizers');
      const snapshot = await getDocs(organizersRef);
      const organizersList = snapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name
      }));
      setOrganizers(organizersList.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error('Error fetching organizers:', error);
    }
  };

  // Fetch guides to get operator organizers
  const fetchGuides = async () => {
    try {
      const guidesRef = collection(firestore, 'Guides');
      let guidesQuery;
      
      if (userRole === 'organizer' && userId) {
        // If organizer, only fetch their guides
        guidesQuery = query(guidesRef, where('organizerId', '==', userId));
      } else {
        guidesQuery = collection(firestore, 'Guides');
      }
      
      const guidesSnapshot = await getDocs(guidesQuery);
      
      const opOrganizerMap: Record<string, { id: string, name: string }> = {};
      
      for (const guideDoc of guidesSnapshot.docs) {
        const guideData = guideDoc.data();
        if (guideData.organizerId) {
          // Get organizer name
          const organizerRef = doc(firestore, 'Organizers', guideData.organizerId);
          const organizerDoc = await getDoc(organizerRef);
          
          if (organizerDoc.exists()) {
            opOrganizerMap[guideDoc.id] = {
              id: guideData.organizerId,
              name: organizerDoc.data().name || 'Unknown'
            };
          }
        }
      }
      
      setOperatorOrganizers(opOrganizerMap);
    } catch (error) {
      console.error('Error fetching guides:', error);
    }
  };

  // Fetch operation list
  const fetchOperationList = async () => {
    setIsOperationLoading(true);
    setError(null);
    try {
      const operationRef = collection(firestore, 'operationlist');
      let entries: Record<string, OperationEntry> = {};
      
      if (userRole === 'admin') {
        // Admin gets all entries
        const snapshot = await getDocs(operationRef);
        snapshot.forEach(doc => {
          entries[doc.id] = { ...doc.data(), id: doc.id } as OperationEntry;
        });
      } 
      else if (userRole === 'guide' && userId) {
        // Guide gets entries where they are either the guide OR the operator
        // First query - where they are the guide
        const guideQuery = query(operationRef, where('guideId', '==', userId));
        const guideSnapshot = await getDocs(guideQuery);
        
        guideSnapshot.forEach(doc => {
          entries[doc.id] = { ...doc.data(), id: doc.id } as OperationEntry;
        });
        
        // Second query - where they are the operator
        const operatorQuery = query(operationRef, where('operatorId', '==', userId));
        const operatorSnapshot = await getDocs(operatorQuery);
        
        operatorSnapshot.forEach(doc => {
          // Only add if not already added from first query
          if (!entries[doc.id]) {
            entries[doc.id] = { ...doc.data(), id: doc.id } as OperationEntry;
          }
        });
      } 
      else if (userRole === 'organizer' && userId) {
        // Organizer gets entries where their organization is either the owner OR the operator
        // First query - where they are the owner org
        const ownerQuery = query(operationRef, where('organizerId', '==', userId));
        const ownerSnapshot = await getDocs(ownerQuery);
        
        ownerSnapshot.forEach(doc => {
          entries[doc.id] = { ...doc.data(), id: doc.id } as OperationEntry;
        });
        
        // Second query - where they are the operator org
        const operatorQuery = query(operationRef, where('operatorOrganizerId', '==', userId));
        const operatorSnapshot = await getDocs(operatorQuery);
        
        operatorSnapshot.forEach(doc => {
          // Only add if not already added from first query
          if (!entries[doc.id]) {
            entries[doc.id] = { ...doc.data(), id: doc.id } as OperationEntry;
          }
        });
      }
      
      console.log(`Total operation entries loaded: ${Object.keys(entries).length}`);
      setOperationList(entries);
    } catch (error) {
      console.error('Error fetching operation list:', error);
      setError(error instanceof Error ? error.message : 'An unexpected error occurred');
    } finally {
      setIsOperationLoading(false);
    }
  };

  // Get unique countries from operation list for filter (for admin only)
  const countries = userRole === 'admin' ? 
    Array.from(
      new Set(Object.values(operationList).map((entry) => entry.residenceCountry))
    ).sort() : [];

  // Process entries for display - for admin, create two entries for each pilgrim
  const processEntriesForDisplay = (entries: OperationEntry[]) => {
    if (userRole !== 'admin') {
      return entries;
    }

    const displayEntries: Array<OperationEntry & { displayRole: string }> = [];
    const processedPilgrims = new Set<string>();

    entries.forEach(entry => {
      // Default isOwner to true if not explicitly false
      const isOwner = entry.isOwner === false ? false : true;
      
      // Check if guide and operator are different
      const hasDifferentOperator = entry.operatorId && entry.guideId !== entry.operatorId;
      
      // Get organizer names for comparison
      const guideOrganizerName = entry.organizerName || '';
      const operatorOrganizerName = entry.operatorOrganizerName || entry.organizerName || '';
      
      // Determine if guide and operator organizers are the same
      const isSameOrganizer = guideOrganizerName === operatorOrganizerName;

      // If organizer filter is applied, only show the relevant role entry
      if (selectedOrganizer) {
        const isSelectedOrganizerOwner = entry.organizerId === selectedOrganizer;
        const isSelectedOrganizerOperator = entry.operatorOrganizerId === selectedOrganizer;

        // Create a unique ID for this pilgrim to prevent duplicates
        const pilgrimKey = entry.pilgrimId;

        // If this pilgrim has already been processed, skip
        if (processedPilgrims.has(pilgrimKey)) {
          return;
        }

        if (isSelectedOrganizerOwner && isSelectedOrganizerOperator) {
          // If selected organizer is both owner and operator
          displayEntries.push({
            ...entry,
            displayRole: isOwner ? 'owner and operator' : 'not owner and operator'
          });
          processedPilgrims.add(pilgrimKey);
        } else if (isSelectedOrganizerOwner) {
          // If selected organizer is just the owner
          displayEntries.push({
            ...entry,
            displayRole: isOwner ? 'owner' : 'not owner'
          });
          processedPilgrims.add(pilgrimKey);
        } else if (isSelectedOrganizerOperator) {
          // If selected organizer is just the operator
          displayEntries.push({
            ...entry,
            displayRole: 'operator'
          });
          processedPilgrims.add(pilgrimKey);
        }
      } else {
        // No organizer filter applied - show all roles
        if (isSameOrganizer) {
          // Add just one entry with "Owner and Operator" or "Not Owner and Operator" role
          displayEntries.push({
            ...entry,
            displayRole: isOwner ? 'owner and operator' : 'not owner and operator'
          });
        } else {
          // Add two separate entries for each pilgrim
          displayEntries.push({
            ...entry,
            displayRole: isOwner ? 'owner' : 'not owner'
          });
          
          if (hasDifferentOperator) {
            displayEntries.push({
              ...entry,
              displayRole: 'operator'
            });
          }
        }
      }
    });

    return displayEntries;
  };

  // Filter operation list entries based on search and filters
  const filteredEntries = Object.values(operationList).filter((entry) => {
    const matchesSearch =
      !searchTerm ||
      entry.pilgrimName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(entry.passportNumber)
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      entry.guideName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.organizerName.toLowerCase().includes(searchTerm.toLowerCase());

    // For organizers, don't use country filter (they only see their own country)
    const matchesCountry = userRole === 'organizer' || !selectedCountry || entry.residenceCountry === selectedCountry;
    
    // For organizers, don't use organizer filter (they only see their own organization)
    const matchesOrganizer = userRole === 'organizer' || !selectedOrganizer || 
      entry.organizerId === selectedOrganizer || 
      entry.operatorOrganizerId === selectedOrganizer;
    
    return matchesSearch && matchesCountry && matchesOrganizer;
  });

  // Process entries for display purposes (add dual entries for admin view)
  const displayEntries = processEntriesForDisplay(filteredEntries);
  
  // Apply role filter
  const roleFilteredEntries = selectedRole ? 
    displayEntries.filter(entry => {
      // For admin view, use displayRole that was assigned during processing
      if (userRole === 'admin' && (entry as any).displayRole) {
        return (entry as any).displayRole === selectedRole;
      }
      
      // For other views, determine role dynamically
      const role = determineRoleForUser(entry);
      return role === selectedRole;
    }) : 
    displayEntries;
  
  // Calculate pagination
  const totalPages = Math.ceil(roleFilteredEntries.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedEntries = roleFilteredEntries.slice(startIndex, startIndex + itemsPerPage);
  
  // Calculate statistics for roles
  const roleStats = (() => {
    const stats: Record<string, number> = {
      'owner': 0,
      'not owner': 0,
      'operator': 0,
      'owner and operator': 0,
      'not owner and operator': 0,
      'unknown': 0
    };
    
    displayEntries.forEach(entry => {
      const role = userRole === 'admin' && (entry as any).displayRole 
        ? (entry as any).displayRole 
        : determineRoleForUser(entry);
      
      stats[role] = (stats[role] || 0) + 1;
    });
    
    return stats;
  })();
  
  // Calculate statistics for guides and packages
  const guideAndPackageStats = (() => {
    const guideCounts: Record<string, number> = {};
    const packageCounts: Record<string, number> = {};
    
    displayEntries.forEach(entry => {
      // Count guides
      if (entry.guideName) {
        guideCounts[entry.guideName] = (guideCounts[entry.guideName] || 0) + 1;
      }
      
      // Count packages
      if (entry.packageName) {
        packageCounts[entry.packageName] = (packageCounts[entry.packageName] || 0) + 1;
      }
    });
    
    // Sort guides by count (descending)
    const topGuides = Object.entries(guideCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5); // Top 5
      
    // Sort packages by count (descending)
    const topPackages = Object.entries(packageCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5); // Top 5
      
    return {
      totalGuides: Object.keys(guideCounts).length,
      topGuides,
      totalPackages: Object.keys(packageCounts).length,
      topPackages
    };
  })();

  // Handle sync to operation list
  const handleSyncToOperation = async () => {
    if (userRole !== 'admin') {
      toast.error('Only admin can perform this operation');
      return;
    }

    setIsSyncing(true);
    setSyncProgress({ current: 0, total: Object.keys(links).length });
    setError(null);
    
    try {
      const approvedLinks = Object.values(links).filter(link => link.status === 'approved');
      setSyncProgress({ current: 0, total: approvedLinks.length });
      
      // Use batch writes to improve performance
      let batch = writeBatch(firestore);
      let operationCount = 0;
      let totalProcessed = 0;
      
      for (const link of approvedLinks) {
        // Check if already in operation list
        const existingOperationId = Object.entries(operationList).find(
          ([_, op]) => op.pilgrimId === link.pilgrimId
        )?.[0];
        
        // Create operation data from link
        const operationData = {
          pilgrimId: link.pilgrimId,
          pilgrimName: link.pilgrimName,
          passportNumber: link.passportNumber,
          gender: link.gender,
          residenceCountry: link.residenceCountry,
          packageName: link.packageName,
          guideId: link.guideId,
          guideName: link.guideName,
          organizerId: link.organizerId,
          organizerName: link.organizerName,
          syncedAt: Date.now(),
          isOwner: link.isOwner === false ? false : true // Default to true if not explicitly false
        };
        
        if (existingOperationId) {
          // Update existing operation
          const docRef = doc(firestore, 'operationlist', existingOperationId);
          batch.update(docRef, operationData);
        } else {
          // Create new operation
          const docRef = doc(collection(firestore, 'operationlist'));
          batch.set(docRef, operationData);
        }
        
        operationCount++;
        totalProcessed++;
        
        // Update progress
        setSyncProgress({ current: totalProcessed, total: approvedLinks.length });
        
        // Commit batch when it reaches max size
        if (operationCount >= 450) { // Firestore limit is 500 operations
          await batch.commit();
          batch = writeBatch(firestore);
          operationCount = 0;
          console.log(`Committed batch: ${totalProcessed}/${approvedLinks.length}`);
        }
      }
      
      // Commit any remaining operations
      if (operationCount > 0) {
        await batch.commit();
      }
      
      // Refresh operation list
      await fetchOperationList();
      
      toast.success(`Successfully synced ${totalProcessed} pilgrims to operation list`);
    } catch (error) {
      console.error('Error syncing to operation list:', error);
      setError(error instanceof Error ? error.message : 'Failed to sync operation list');
      toast.error('Failed to sync operation list');
    } finally {
      setIsSyncing(false);
    }
  };

  // Export operation list
  const handleExportData = () => {
    try {
      setIsExporting(true);
      
      // Prepare export data
      const exportData = displayEntries.map(entry => {
        // Handle role determination based on user type
        const role = userRole === 'admin' ? 
          (entry as any).displayRole || determineRole(entry) : 
          determineRoleForUser(entry);
        
        return {
          'Pilgrim Name': entry.pilgrimName,
          'Passport Number': entry.passportNumber,
          'Gender': entry.gender === 'male' ? 'Male' : 'Female',
          'Country': entry.residenceCountry,
          'Package': entry.packageName,
          'Guide': entry.guideName,
          'Guide Organizer': entry.organizerName,
          'Operator': entry.operatorName || entry.guideName,
          'Operator Organizer': entry.operatorOrganizerName || entry.organizerName,
          'Role': role,
          'Sync Date': new Date(entry.syncedAt).toLocaleDateString()
        };
      });
      
      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);
      
      // Set column widths
      ws['!cols'] = [
        { wch: 30 }, // Pilgrim Name
        { wch: 20 }, // Passport Number
        { wch: 10 }, // Gender
        { wch: 20 }, // Country
        { wch: 25 }, // Package
        { wch: 25 }, // Guide
        { wch: 25 }, // Guide Organizer
        { wch: 25 }, // Operator
        { wch: 25 }, // Operator Organizer
        { wch: 20 }, // Role
        { wch: 15 }, // Sync Date
      ];
      
      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Operation List');
      
      // Generate filename with current date
      const date = new Date().toISOString().split('T')[0];
      const filename = `operation-list-${date}.xlsx`;
      
      // Save file
      XLSX.writeFile(wb, filename);
      
      toast.success('Data exported successfully');
    } catch (error: any) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    } finally {
      setIsExporting(false);
    }
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'operator':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-800">
            <Radio size={12} className="mr-1" />
            Operator
          </span>
        );
      case 'owner':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-800">
            <User size={12} className="mr-1" />
            Owner
          </span>
        );
      case 'not owner':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-50 text-yellow-800">
            <User size={12} className="mr-1" />
            Not Owner
          </span>
        );
      case 'owner and operator':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-800">
            <Users size={12} className="mr-1" />
            Owner & Operator
          </span>
        );
      case 'not owner and operator':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-50 text-orange-800">
            <Users size={12} className="mr-1" />
            Not Owner & Operator
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-800">
            Unknown
          </span>
        );
    }
  };

  if (linksLoading && !initialized || isOperationLoading) {
    return (
      <div className="space-y-6">
        {/* Show progress for links loading or syncing */}
        {(linksLoading || isSyncing) && (
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <LoadingProgress
              progress={isSyncing ? syncProgress.current : progress.current}
              total={isSyncing ? syncProgress.total : progress.total}
              label={isSyncing ? 'Syncing operation list...' : 'Loading links...'}
            />
          </div>
        )}
        <LoadingSpinner />
      </div>
    );
  }

  if (error) return <div className="text-red-500">{error}</div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-50 rounded-lg">
            <List className="text-primary-500" size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {translations?.operationList?.title || 'My Operation List'}
            </h1>
            <p className="text-gray-500 mt-1">
              {translations?.operationList?.description || 'View all pilgrims in your operation list'}
            </p>
          </div>
        </div>

        <div className="flex gap-3 flex-wrap">
          {/* Bulk Transfer Operators Button - For admin and organizer */}
          {(userRole === 'admin' || userRole === 'organizer') && Object.keys(operationList).length > 0 && (
            <button
              onClick={() => setIsBulkTransferModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
            >
              <ExternalLink size={18} />
              <span>Bulk Transfer Operators</span>
            </button>
          )}

          {/* Export Button */}
          <button
            onClick={handleExportData}
            disabled={isExporting}
            className={`flex items-center gap-2 px-4 py-2 bg-primary-50 text-primary-600 rounded-lg ${
              isExporting ? 'bg-gray-100 cursor-not-allowed' : 'hover:bg-primary-100'
            } transition-colors`}
          >
            <Download size={18} className={isExporting ? 'animate-spin' : ''} />
            <span>
              {isExporting ? 'Exporting...' : translations?.operationList?.export || 'Export Data'}
            </span>
          </button>

          {/* Admin Sync Button */}
          {userRole === 'admin' && (
            <button
              onClick={handleSyncToOperation}
              disabled={isSyncing}
              className={`flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg 
                      ${isSyncing ? 'bg-gray-400 cursor-not-allowed' : 'hover:bg-primary-600'} 
                      transition-colors`}
            >
              {isSyncing ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  <span>{translations?.operationList?.syncing || 'Syncing...'}</span>
                </>
              ) : (
                <>
                  <RefreshCw size={20} />
                  <span>{translations?.operationList?.syncButton || 'Sync Operation List'}</span>
                </>
              )}
            </button>
          )}

          {/* Toggle Stats Button */}
          <button
            onClick={() => setShowStats(!showStats)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            {showStats ? (
              <>
                <PieChart size={18} />
                <span>Hide Stats</span>
              </>
            ) : (
              <>
                <BarChart size={18} />
                <span>Show Stats</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Statistics Section */}
      {showStats && (
        <div className="space-y-6">
          {/* Role Statistics */}
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Users size={20} className="text-primary-500" />
              Role Distribution
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
              {Object.entries(roleStats).map(([role, count]) => (
                count > 0 && (
                  <div key={role} className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-1.5">
                        {getRoleBadge(role)}
                      </div>
                      <span className="text-xl font-bold">{count}</span>
                    </div>
                    <div className="w-full bg-gray-200 h-1.5 rounded-full">
                      <div 
                        className={`h-full rounded-full ${
                          role === 'owner' ? 'bg-green-500' :
                          role === 'not owner' ? 'bg-yellow-500' :
                          role === 'operator' ? 'bg-blue-500' :
                          role === 'owner and operator' ? 'bg-purple-500' :
                          role === 'not owner and operator' ? 'bg-orange-500' :
                          'bg-gray-500'
                        }`} 
                        style={{ width: `${Math.round((count / displayEntries.length) * 100)}%` }}
                      ></div>
                    </div>
                    <div className="text-xs text-gray-500 mt-1 text-right">
                      {Math.round((count / displayEntries.length) * 100)}% of total
                    </div>
                  </div>
                )
              ))}
            </div>
          </div>

          {/* Guide and Package Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Guide Statistics */}
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <User size={20} className="text-primary-500" />
                Guide Statistics ({guideAndPackageStats.totalGuides} total)
              </h2>
              <div className="space-y-3">
                {guideAndPackageStats.topGuides.map(([name, count], index) => (
                  <div key={name} className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium truncate max-w-[70%]">{name}</span>
                      <span className="text-sm font-bold">{count}</span>
                    </div>
                    <div className="w-full bg-gray-200 h-1.5 rounded-full">
                      <div 
                        className="bg-blue-500 h-full rounded-full"
                        style={{ 
                          width: `${Math.round((count / displayEntries.length) * 100)}%`,
                          opacity: 1 - (index * 0.15)
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Package Statistics */}
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Package size={20} className="text-primary-500" />
                Package Statistics ({guideAndPackageStats.totalPackages} total)
              </h2>
              <div className="space-y-3">
                {guideAndPackageStats.topPackages.map(([name, count], index) => (
                  <div key={name} className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium truncate max-w-[70%]">{name}</span>
                      <span className="text-sm font-bold">{count}</span>
                    </div>
                    <div className="w-full bg-gray-200 h-1.5 rounded-full">
                      <div 
                        className="bg-green-500 h-full rounded-full"
                        style={{ 
                          width: `${Math.round((count / displayEntries.length) * 100)}%`,
                          opacity: 1 - (index * 0.15)
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters Section */}
      <div className="bg-white p-4 rounded-lg shadow-sm space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search by name, passport, guide..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          </div>
          
          {/* Role Filter */}
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Roles</option>
            {Object.entries(roleStats).map(([role, count]) => (
              count > 0 && (
                <option key={role} value={role}>
                  {role.charAt(0).toUpperCase() + role.slice(1)} ({count})
                </option>
              )
            ))}
          </select>
          
          {/* Country Filter - Admin Only */}
          {userRole === 'admin' && (
            <select
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Countries</option>
              {countries.map((country) => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>
          )}
          
          {/* Organizer Filter - Admin Only */}
          {userRole === 'admin' && (
            <select
              value={selectedOrganizer}
              onChange={(e) => setSelectedOrganizer(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Organizers</option>
              {organizers.map((org) => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          )}
        </div>

        {/* Active Filters */}
        {(searchTerm || selectedRole || selectedCountry || selectedOrganizer) && (
          <div className="flex items-center gap-2 pt-2">
            <Filter size={16} className="text-primary-500" />
            <span className="text-sm text-gray-600">
              Active Filters:
            </span>
            <div className="flex flex-wrap gap-2">
              {searchTerm && (
                <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                  Search: {searchTerm}
                </span>
              )}
              {selectedRole && (
                <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                  Role: {selectedRole}
                </span>
              )}
              {selectedCountry && (
                <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                  Country: {selectedCountry}
                </span>
              )}
              {selectedOrganizer && (
                <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                  Organizer: {organizers.find(o => o.id === selectedOrganizer)?.name || selectedOrganizer}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pilgrim
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Package
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Guide
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Operator
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedEntries.length > 0 ? (
                paginatedEntries.map((entry) => (
                  <tr key={entry.id + ((entry as any).displayRole || '')} className="hover:bg-gray-50">
                    {/* Pilgrim Info */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-500">
                          <User size={20} />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{entry.pilgrimName}</div>
                          <div className="text-sm text-gray-500">{entry.passportNumber}</div>
                          <div className="text-sm text-gray-500">
                            <MapPin size={12} className="inline mr-1" />
                            {entry.residenceCountry}
                          </div>
                        </div>
                      </div>
                    </td>
                    
                    {/* Package */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{entry.packageName}</div>
                    </td>
                    
                    {/* Guide Info */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <div className="flex items-center">
                          <User size={14} className="text-primary-500 mr-1" />
                          <div className="text-sm font-medium text-gray-900">{entry.guideName}</div>
                        </div>
                        <div className="flex items-center mt-1">
                          <Building2 size={14} className="text-gray-400 mr-1" />
                          <div className="text-sm text-gray-500">{entry.organizerName}</div>
                        </div>
                      </div>
                    </td>
                    
                    {/* Operator Info */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <div className="flex items-center">
                          <Radio size={14} className="text-blue-500 mr-1" />
                          <div className="text-sm font-medium text-gray-900">
                            {entry.operatorName || entry.guideName}
                          </div>
                        </div>
                        <div className="flex items-center mt-1">
                          <Building2 size={14} className="text-gray-400 mr-1" />
                          <div className="text-sm text-gray-500">
                            {entry.operatorOrganizerName || entry.organizerName}
                          </div>
                        </div>
                      </div>
                    </td>
                    
                    {/* Role */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      {userRole === 'admin' && (entry as any).displayRole
                        ? getRoleBadge((entry as any).displayRole)
                        : getRoleBadge(determineRoleForUser(entry))}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                    No pilgrims found matching the current filters
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {roleFilteredEntries.length > itemsPerPage && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md ${
                  currentPage === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <ChevronRight size={16} className="mr-1" />
                Previous
              </button>
              <span className="text-sm text-gray-700">
                Page {currentPage} of {totalPages}
                <span className="mx-2">•</span>
                Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, roleFilteredEntries.length)} of {roleFilteredEntries.length} results
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md ${
                  currentPage === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Next
                <ChevronLeft size={16} className="ml-1" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Transfer Modal */}
      <BulkTransferOperatorModal
        isOpen={isBulkTransferModalOpen}
        onClose={() => setIsBulkTransferModalOpen(false)}
      />
    </div>
  );
};

export default MyOperationListPage;